import { inject, Injectable } from '@angular/core';
import { Option, Result } from '@sapphire/result';
import { match } from 'ts-pattern';

import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';
import {
  canBypassLoiThreshold,
  classifySurvey,
  GROUPED_MESSAGES,
  MC_MESSAGES,
  QbpSaveContext,
  StandardSurveyPayloadInput,
  Survey,
  SURVEY_CLASSIFICATION_KIND,
  SurveyKind,
  SurveyStatusInfo,
  TRACKER_MESSAGES,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import { actingUser } from '../../state/core/utils/survey-save-pipeline.helper';
import {
  buildProjectUpdate,
  ProjectUpdateInput,
} from '../../state/audience/utils/project-save.helper';
import { marginSnapshot } from '../../state/audience/domain/margin-snapshot';
import {
  SurveyUpdateOverrides,
  ValidatedSurveyForSave,
} from '../../state/core/pipeline/survey-save.pipeline.types';
import { ProjectUpdateInputBuilder } from './project-update-input.builder';
import { SurveyUpdateRequest } from './survey-persistence.service';

// Builds the request a save writes for one survey, from that survey's own state.
@Injectable({ providedIn: 'root' })
export class SurveySaveInputBuilder {
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);
  private readonly _projectUpdateInput = inject(ProjectUpdateInputBuilder);

  // A project writes every child at once; anything else, the active survey.
  // Overrides only reach a standard survey.
  public request(
    validated: ValidatedSurveyForSave,
    survey: Nullable<Survey>,
    overrides: SurveyUpdateOverrides,
  ): Result<SurveyUpdateRequest, string> {
    return survey && SurveyKind.isProject(survey)
      ? this._projectUpdateRequest(survey)
      : Result.ok(this._surveyUpdateRequest(validated, survey, overrides));
  }

  // Shared by every project type; each turns the input into its own request.
  private _projectUpdateRequest(
    survey: Survey,
  ): Result<SurveyUpdateRequest, string> {
    return this._projectUpdateInput
      .build({
        noChildrenSelected: MC_MESSAGES.NO_LOCALES_SELECTED,
        childNotSaveable: MC_MESSAGES.LOCALE_NOT_SAVEABLE,
      })
      .map((built) => this._projectRequestFor(survey, built))
      .unwrapOr(Result.err(MC_MESSAGES.LOCALE_NOT_SAVEABLE));
  }

  // `kind` picks the endpoint, so it is chosen together with its input.
  private _projectRequestFor(
    survey: Survey,
    context: Omit<ProjectUpdateInput, 'messages'>,
  ): Result<SurveyUpdateRequest, string> {
    return match(classifySurvey(survey).kind)
      .with(SURVEY_CLASSIFICATION_KIND.GROUP_MC, (kind) =>
        buildProjectUpdate({
          ...context,
          messages: {
            noChildrenSelected: MC_MESSAGES.NO_LOCALES_SELECTED,
            childNotSaveable: MC_MESSAGES.LOCALE_NOT_SAVEABLE,
          },
        }).map(
          (input): SurveyUpdateRequest => ({
            kind,
            projectId: context.project.id,
            input,
          }),
        ),
      )
      .with(SURVEY_CLASSIFICATION_KIND.TRACKER, (kind) =>
        buildProjectUpdate({
          ...context,
          messages: {
            noChildrenSelected: TRACKER_MESSAGES.SELECT_AT_LEAST_TWO_WAVES,
            childNotSaveable: TRACKER_MESSAGES.WAVE_NOT_SAVEABLE,
          },
        }).map(
          (input): SurveyUpdateRequest => ({
            kind,
            projectId: context.project.id,
            input,
          }),
        ),
      )
      .with(SURVEY_CLASSIFICATION_KIND.GROUPED, (kind) =>
        buildProjectUpdate({
          ...context,
          messages: {
            noChildrenSelected: GROUPED_MESSAGES.NO_SURVEYS_SELECTED,
            childNotSaveable: GROUPED_MESSAGES.SURVEY_NOT_SAVEABLE,
          },
        }).map(
          (input): SurveyUpdateRequest => ({
            kind,
            projectId: context.project.id,
            input,
          }),
        ),
      )
      .otherwise(
        (): Result<SurveyUpdateRequest, string> =>
          Result.err(MC_MESSAGES.LOCALE_NOT_SAVEABLE),
      );
  }

  // What updateStandardSurvey expects.
  private _surveyUpdateRequest(
    validated: ValidatedSurveyForSave,
    survey: Nullable<Survey>,
    overrides: SurveyUpdateOverrides,
  ): SurveyUpdateRequest {
    return {
      kind: SURVEY_CLASSIFICATION_KIND.STANDARD,
      surveyId: validated.surveyId,
      // The effect's overrides win.
      input: {
        ...this._standardSurveyInput(validated),
        ...this._qbpContext(survey),
        ...validated.triggerOverrides,
        ...overrides,
      },
    };
  }

  // Not loaded yet, or no QBP saved ⇒ no context: the launch form alone decides.
  private _qbpContext(
    survey: Nullable<Survey>,
  ): Pick<StandardSurveyPayloadInput, 'qbpContext'> {
    return Option.from(survey)
      .andThen((loadedSurvey) => this._qbpContextFor(loadedSurvey))
      .mapOr({}, (qbpContext) => ({ qbpContext }));
  }

  private _qbpContextFor(survey: Survey): Option<QbpSaveContext> {
    return Option.from(survey.qbp).map((stored) => ({
      stored,
      isQbpLocked: SurveyStatusInfo.for(survey).isPastDraft(),
    }));
  }

  private _standardSurveyInput({
    data,
    launch,
    marginForm,
    pricing,
    advancedFeasibilityFilters,
    respondentListPsids,
    locationFileUpload,
  }: ValidatedSurveyForSave): StandardSurveyPayloadInput {
    const user = actingUser(this._authSelectors);
    const { metadata, qualifications, quotas } = data;

    return {
      metadata,
      pricing,
      userId: user.id,
      companyId: user.companyId,
      targeting: {
        qualifications,
        quotas,
        locationFileUpload,
      },
      advancedFeasibilityFilters,
      respondentListPsids,
      enableRespondentStatusFilter:
        !!this._userSettingsSelectors.buyerSettings()
          .enableRespondentStatusFilter,
      launch,
      launchAccess: {
        canBypassLoiThreshold: canBypassLoiThreshold({
          isOperator: this._authSelectors.isOperator(),
          accessLevel: this._authSelectors.userAccessLevel(),
          isFeatureEnabled:
            !!this._userSettingsSelectors.buyerSettings()
              .enableBypassLOIThreshold,
        }),
        canEditServiceOperator: this._authSelectors.isOperator(),
      },
      ...marginSnapshot(marginForm).mapOr({}, (snapshot) => ({
        margin: snapshot,
      })),
    };
  }
}
